import type { Logger } from '@mimir/common/types/logger';
import type { Fraction } from '@mimir/common/util/fractionType';
import type { CommandFromMain, MessageFromMain, MessageFromWorker, RequestFromMain, ResponseFromWorker, StatusUpdate } from './cutterMediaPlayerProtocol';
import { invariant } from '@mimir/common/util/invariant';
import type { SegmentProvider } from '@mimir/cutter/frameProvider/demuxAndDecode/segmentedPacketProvider';
import type { AudioHandler } from '@mimir/cutter/player/audioHandler';
import { createItemSegmentMainHandler } from '../worker/mainHandlers/itemSegmentMainHandler';
import type { ItemSegmentMessageFromMain } from '../worker/protocol/itemSegmentProtocol';
import { createAudioMainHandler } from '../worker/mainHandlers/audioMainHandler';
import type { AudioRequestFromMain } from '../worker/protocol/audioProtocol';
import type { BufferCache } from '@mimir/common/util/bufferCache';
import type { BufferRange } from '@mimir/cutter/frameProvider/frameProvider';
import type { VisualizationMode } from '../audioVisualizer';
import type { AudioLevelsData } from '@mimir/cutter/player/cutterCorePlayer';

export interface LoadItemResult {
  duration: Fraction,
  frameRate: Fraction,
  videoWidth: number | null,
  videoHeight: number | null,
}

export interface MediaPlayer {
  loadItem(
    itemId: string,
    ignoreVideo: boolean,
    ignoreAudio: boolean,
    initialMuteStates: boolean[]
  ): Promise<LoadItemResult>;
  setPosition(position: Fraction): Promise<void>;
  setPlaybackRate(speedFactor: Fraction): void;
  stepOneFrameBackward(): void;
  stepOneFrameForward(): void;
  setAudioTrackMuteState(trackIndex: number, muted: boolean): void;
  setAudioVisualizationMode(mode: VisualizationMode | null): void;
  setVolume(volume: number): void;
  setMuted(muted: boolean): void;
  setClampedPlaybackRange(range: { start: Fraction, end: Fraction } | null): void;
  clear(): void;
}

export interface MediaPlayerListener {
  onPlay(): void,
  onPause(): void,
  onPositionChanged(position: Fraction): void,
  onDurationChanged(duration: Fraction): void,
  onBuffered(bufferedRanges: BufferRange[]): void,
  onMuteStatesChanged(muteStates: boolean[]): void,
  onAudioVisualizationModeChanged(mode: VisualizationMode | null, available: (VisualizationMode | null)[]): void;
  onAudioLevelsData(data: AudioLevelsData): void;
  onVirtualClipRangeChanged(range: { start: Fraction, end: Fraction } | null): void;
}

export interface CutterMediaPlayerServices {
  bufferCache: BufferCache,
  logger: Logger,
  createSegmentProviderForItem: (itemId: string) => SegmentProvider,
}

export interface CutterMediaPlayerParameters {
  services: CutterMediaPlayerServices,
  listener: MediaPlayerListener,
  workerProcessorUrl: string,
  canvas: HTMLCanvasElement,
  audioHandler: AudioHandler,
  prematureVideoFrameReuseBugWorkaround: boolean,
}

export function createCutterMediaPlayer(parameters: CutterMediaPlayerParameters): MediaPlayer {
  return createCutterMediaPlayerPrivate(parameters);
}


interface PendingRequest {
  resolve: (response: ResponseFromWorker) => void,
  reject: (error: unknown) => void,
}

function createCutterMediaPlayerPrivate(parameters: CutterMediaPlayerParameters): MediaPlayer {
  const { canvas, services, listener, workerProcessorUrl, audioHandler, prematureVideoFrameReuseBugWorkaround } = parameters;
  const { bufferCache, logger, createSegmentProviderForItem } = services;

  const worker = new Worker(workerProcessorUrl, { type: 'module' });

  const postMessageFromMain = (message: MessageFromMain, transfer?: Transferable[]) => {
    if (transfer !== undefined) {
      worker.postMessage(message, { transfer });
    } else {
      worker.postMessage(message);
    }
  };

  const updateResolution = () => {
    const rect = canvas.getBoundingClientRect();
    const height = rect.height * window.devicePixelRatio;
    const width = rect.width * window.devicePixelRatio;
    // If the player is hidden it will have size of zero. Let's not confuse the
    // player by passing in nonsense values
    if ((width !== 0) && (height !== 0)) {
      postMessageFromMain({
        type: 'command',
        command: {
          type: 'update_display_resolution',
          width,
          height,
        }
      });
    }
    setTimeout(updateResolution, 100);
  };

  const postCommandFromMain = (command: CommandFromMain, transfer?: Transferable[]): void => {
    postMessageFromMain({ type: 'command', command }, transfer);
  };

  let requestIdCounter : number = 0;
  const pendingRequests = new Map<number, PendingRequest>();

  const postRequestFromMain = async (request: RequestFromMain): Promise<ResponseFromWorker> => {
    const requestId = requestIdCounter++;
    return await new Promise<ResponseFromWorker>((resolve, reject) => {
      pendingRequests.set(requestId, { resolve, reject });
      postMessageFromMain({
        type: 'request',
        requestId,
        request,
      });
    });
  };

  const handleWorkerResponse = (requestId: number, response: ResponseFromWorker) => {
    const pendingRequest = pendingRequests.get(requestId);
    if (pendingRequest === undefined) {
      logger.warn(`Unexpected response for requestId: ${requestId}`);
      return;
    }
    pendingRequests.delete(requestId);
    if (response.type === 'error') {
      const error = new Error(response.message);
      error.stack = response.stack;
      error.name = response.name;
      pendingRequest.reject(error);
    } else {
      pendingRequest.resolve(response);
    }
  };

  const handleItemSegmentRequestFromWorker =
    createItemSegmentMainHandler(
      createSegmentProviderForItem,
      (message: ItemSegmentMessageFromMain) => {
        postMessageFromMain({ type: 'item_segment', message });
      },
    );

  const handleAudioRequestFromWorker =
    createAudioMainHandler(
      { bufferCache },
      audioHandler,
      (request: AudioRequestFromMain, transfer?: Transferable[]): void => {
        postMessageFromMain({ type: 'audio', request }, transfer);
      }
    );

  const handleStatusUpdateFromWorker = (statusUpdate: StatusUpdate) => {
    const statusUpdateType = statusUpdate.type;
    switch (statusUpdateType) {
      case 'playing': {
        if (statusUpdate.playing) {
          listener.onPlay();
        } else {
          listener.onPause();
        }
        break;
      }
      case 'position': {
        listener.onPositionChanged(statusUpdate.position);
        break;
      }
      case 'duration_changed': {
        listener.onDurationChanged(statusUpdate.duration);
        break;
      }
      case 'buffered': {
        listener.onBuffered(statusUpdate.bufferedRanges);
        break;
      }
      case 'mute_states': {
        listener.onMuteStatesChanged(statusUpdate.muteStates);
        break;
      }
      case 'audio_visualization_mode': {
        listener.onAudioVisualizationModeChanged(statusUpdate.mode, statusUpdate.available);
        break;
      }
      case 'audio_levels_data': {
        listener.onAudioLevelsData(statusUpdate.data);
        break;
      }
      case 'virtual_clip_range': {
        listener.onVirtualClipRangeChanged(statusUpdate.range);
        break;
      }
      default:
        statusUpdateType satisfies never;
    }
  };

  worker.onmessage = ((event: MessageEvent<MessageFromWorker>) => {
    const message = event.data;
    const messageType = message.type;
    switch (messageType) {
      case 'response': {
        handleWorkerResponse(message.requestId, message.response);
        break;
      }
      case 'item_segment': {
        handleItemSegmentRequestFromWorker(message.message);
        break;
      }
      case 'audio': {
        handleAudioRequestFromWorker(message.request);
        break;
      }
      case 'status_update': {
        handleStatusUpdateFromWorker(message.statusUpdate);
        break;
      }
      default:
        messageType satisfies never;
    }
  });

  const offscreenCanvas = canvas.transferControlToOffscreen();
  postCommandFromMain(
    {
      type: 'initialize',
      offscreenCanvas,
      prematureVideoFrameReuseBugWorkaround,
    },
    [offscreenCanvas]
  );

  updateResolution();

  return {
    loadItem: async (itemId: string, ignoreVideo: boolean, ignoreAudio: boolean, initialMuteStates: boolean[]): Promise<LoadItemResult> => {
      const response = await postRequestFromMain({ type: 'load_item', itemId, ignoreVideo, ignoreAudio, initialMuteStates });
      invariant(response.type === 'item_loaded', `Unexpected response from worker: ${response.type}`);
      return { duration: response.duration, frameRate: response.frameRate, videoWidth: response.videoWidth, videoHeight: response.videoHeight };
    },
    setPosition: async (position: Fraction): Promise<void> => {
      const response = await postRequestFromMain({ type: 'set_position', position });
      invariant(response.type === 'ok', `Unexpected response from worker: ${response.type}`);
    },
    setPlaybackRate(speedFactor: Fraction): void {
      postCommandFromMain({ type: 'set_playback_rate', speedFactor });
    },
    stepOneFrameBackward(): void {
      postCommandFromMain({ type: 'step_one_frame_backward' });
    },
    stepOneFrameForward(): void {
      postCommandFromMain({ type: 'step_one_frame_forward' });
    },
    setAudioTrackMuteState(trackIndex: number, muted: boolean): void {
      postCommandFromMain({ type: 'set_audio_track_mute_state', trackIndex, muted });
    },
    setAudioVisualizationMode(mode: VisualizationMode): void {
      postCommandFromMain({ type: 'set_audio_visualization_mode', mode });
    },
    setVolume(volume: number): void {
      postCommandFromMain({ type: 'set_volume', volume });
    },
    setMuted(muted: boolean): void {
      postCommandFromMain({ type: 'set_muted', muted });
    },
    setClampedPlaybackRange(range: { start: Fraction, end: Fraction } | null): void {
      postCommandFromMain({ type: 'set_clamped_playback_range', range });
    },
    clear(): void {
      postCommandFromMain({ type: 'clear' });
    },
  };
}

